import { Check } from 'lucide-react';
import { BottomSheet } from '@/components/BottomSheet/BottomSheet';

/**
 * Specialized BottomSheet с прокручиваемым списком options для single-select pickers.
 * Block 12 PR-B Phase B1.
 *
 * Используется для пиков «выбор фасада», «выбор бренда декора», «выбор типа
 * столешницы» и аналогичных. Composition over inheritance — wraps BottomSheet
 * (не расширяет его).
 *
 * Поведение:
 * - `isOpen=false` → BottomSheet возвращает null.
 * - Click на option → `onSelect(value)`, затем `onClose()` (single-select auto-close).
 * - Selected option визуально выделена (border-cta + bg-cta/10 + Check icon справа).
 * - Empty options array → текст «Нет вариантов» центрированно.
 *
 * P-022 a11y:
 * - listbox / option roles, `aria-selected` per option.
 * - Каждая опция-кнопка `min-h-tap`, `focus-visible:ring-cta`.
 */
export interface PickerOption {
  value: string;
  label: string;
  /** Optional sublabel (например «Премиум» / «ЛДСП» описание). */
  description?: string;
}

export interface PickerSheetProps {
  isOpen: boolean;
  title: string;
  options: ReadonlyArray<PickerOption>;
  /** Текущее value (null = ничего не выбрано). */
  selectedValue: string | null;
  onSelect: (value: string) => void;
  onClose: () => void;
}

export function PickerSheet({
  isOpen,
  title,
  options,
  selectedValue,
  onSelect,
  onClose,
}: PickerSheetProps) {
  const handleSelect = (value: string) => {
    onSelect(value);
    onClose();
  };

  return (
    <BottomSheet isOpen={isOpen} title={title} onClose={onClose}>
      {options.length === 0 ? (
        <p className="text-muted text-sm text-center py-8">Нет вариантов</p>
      ) : (
        <ul role="listbox" aria-label={title} className="flex flex-col gap-2">
          {options.map((opt) => {
            const selected = selectedValue === opt.value;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => handleSelect(opt.value)}
                  className={
                    'w-full min-h-tap flex items-center justify-between gap-3 px-4 py-3 ' +
                    'rounded-xl border-[1.5px] text-left ' +
                    (selected ? 'border-cta bg-cta/10 ' : 'border-accent bg-card ') +
                    'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
                    'hover:bg-accent/5 active:opacity-90'
                  }
                >
                  <span className="flex-1">
                    <span className="block text-base font-semibold text-ink leading-tight">
                      {opt.label}
                    </span>
                    {opt.description ? (
                      <span className="block text-sm text-muted mt-0.5">{opt.description}</span>
                    ) : null}
                  </span>
                  {selected ? (
                    <Check size={20} aria-hidden="true" className="text-cta flex-shrink-0" />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </BottomSheet>
  );
}
